import { ChatBankAccount, ChatCurrency } from './types'

/** Filtre pill'lerinin ve döviz rozetlerinin sabit sırası. */
const CURRENCY_ORDER: ChatCurrency[] = ['TRY', 'USD', 'EUR']

/** Döviz filtresi: tek bir döviz kodu ya da "Tümü". */
export type CurrencyFilter = ChatCurrency | 'ALL'

/** Etiketi (şirket adı ya da banka adı) ve o etikete düşen hesapları taşıyan grup. */
export interface AccountGroup {
  key: string
  accounts: ChatBankAccount[]
}

/**
 * Hesapları verilen anahtara göre, **veride ilk görülme sırasını koruyarak** gruplar.
 * Sıralama backend'in gönderdiği sıraya bırakılır: tab sırası da banka başlığı sırası da buradan gelir.
 */
export function groupInOrder(accounts: ChatBankAccount[], keyOf: (account: ChatBankAccount) => string): AccountGroup[] {
  const groups: AccountGroup[] = []

  for (const account of accounts) {
    const key = keyOf(account)
    const existing = groups.find(group => group.key === key)
    if (existing) existing.accounts.push(account)
    else groups.push({ key, accounts: [account] })
  }
  return groups
}

/** Listede gerçekten bulunan dövizler (filtre barı yalnız bunları gösterir). */
export function availableCurrencies(accounts: ChatBankAccount[]): ChatCurrency[] {
  return CURRENCY_ORDER.filter(currency => accounts.some(account => account.currency === currency))
}

/** "TR150004600763888000038059" → "TR15 0004 6007 6388 8000 0380 59". Kopyalanan değer ham IBAN'dır. */
export function formatIban(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim()
}

/** Ekranda TRY yerine TL yazılır (formatChatMoney ile aynı sezgi). */
export function currencyLabel(currency: ChatCurrency): string {
  return currency === 'TRY' ? 'TL' : currency
}

/** Kartın render etmesi gereken her şeyi taşıyan, tek karar noktasına indirgenmiş görünüm. */
export interface BankView {
  /** Şirket tab'ları (veride ilk görülme sırasıyla). */
  companies: AccountGroup[]
  /** Seçili tab; hiç şirket yoksa `undefined` (kart bu durumda hiç render edilmez). */
  activeCompany: AccountGroup | undefined
  /**
   * Faz-42: aktif şirketin tam ticari unvanı — kart gövdesinde başlık. Yalnız sunucu kısa adı
   * AYRICA gönderdiyse doludur; eski yükte `undefined` (sekme zaten adı gösteriyor).
   */
  activeCompanyTitle: string | undefined
  /** Aktif şirkette gerçekten var olan dövizler (filtre barı yalnız bunları gösterir). */
  currencies: ChatCurrency[]
  /** Kullanıcının seçtiği filtre yeni şirkette geçerli değilse TL'ye, o da yoksa Tümü'ye düşer. */
  activeFilter: CurrencyFilter
  /** true → banka başlıklı gruplar çizilir; false → düz satır listesi. */
  grouped: boolean
  /** Etkin filtreye göre görünür hesaplar. */
  visibleAccounts: ChatBankAccount[]
  /** `grouped` iken banka adına göre gruplanmış hesaplar. */
  bankGroups: AccountGroup[]
  /** Sayaç satırı metni ("12 hesap · 4 banka" / "3 hesap · EUR hesapları"). */
  countLabel: string
}

/**
 * `BankAccountsCard`'ın tüm türetilmiş durumunu hesaplar: aktif şirket/tab, döviz filtresinin
 * geçerliliği, gruplu/düz görünüm kararı ve sayaç metni. Kartın tek "sıradan olmayan" mantığı
 * burada toplanır ki tablo testleriyle doğrulanabilsin (bkz. bankAccounts.test.ts).
 */
export function resolveBankView(accounts: ChatBankAccount[], companyIndex: number, filter: CurrencyFilter): BankView {
  // Faz-42: sekme kısa ada göre kurulur; kısa ad gelmeyen eski yükte companyName'e düşer.
  const companies = groupInOrder(accounts, account => account.companyShortName || account.companyName)
  const activeCompany = companies[companyIndex] ?? companies[0]
  const first = activeCompany?.accounts[0]
  const activeCompanyTitle =
    first?.companyShortName && first.companyName && first.companyName !== first.companyShortName
      ? first.companyName
      : undefined
  const currencies = availableCurrencies(activeCompany?.accounts ?? [])

  // Tab değişince seçili döviz yeni şirkette olmayabilir: TL'ye, o da yoksa Tümü'ye düşülür.
  const activeFilter: CurrencyFilter =
    filter === 'ALL' || currencies.includes(filter) ? filter : currencies.includes('TRY') ? 'TRY' : 'ALL'

  // Tek dövizli şirkette filtre barı da banka başlığı da bilgi taşımaz — düz liste çizilir.
  const singleCurrency = currencies.length <= 1
  const grouped = activeFilter === 'ALL' && !singleCurrency
  const visibleAccounts = !activeCompany
    ? []
    : activeFilter === 'ALL'
    ? activeCompany.accounts
    : activeCompany.accounts.filter(account => account.currency === activeFilter)
  const bankGroups = grouped ? groupInOrder(visibleAccounts, account => account.bankName) : []

  const currencySuffix = (currency: ChatCurrency) => ` · ${currencyLabel(currency)} hesapları`
  // Tek dövizli şirkette pill bar yok: döviz bilgisini sayaç üstlenir (currencies[0]'dan),
  // aksi halde yalnız kullanıcı belirli bir dövize filtrelediyse suffix eklenir.
  const countLabel = grouped
    ? `${visibleAccounts.length} hesap · ${bankGroups.length} banka`
    : `${visibleAccounts.length} hesap${
        singleCurrency
          ? currencies[0]
            ? currencySuffix(currencies[0])
            : ''
          : activeFilter === 'ALL'
          ? ''
          : currencySuffix(activeFilter)
      }`

  return {
    companies,
    activeCompany,
    activeCompanyTitle,
    currencies,
    activeFilter,
    grouped,
    visibleAccounts,
    bankGroups,
    countLabel,
  }
}
